// src/common/form/CreatePatientForm.jsx
import { useState } from "react";
import { Button, FormField, FormSubmit, Dropdown, MutationError } from "@common";
import { LEAD_CATEGORIES } from "@utils/contants";
import { useCreatePatient } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { toast } from "react-hot-toast";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { FORM_IDS, torontoToday } from "@utils";

export default function CreatePatientForm({
  clinicId,
  initialLeadCategory = LEAD_CATEGORIES[0],
  canEdit = true,
}) {
  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.CREATE_PATIENT,
  );

  const [patientName, setPatientName] = useState("");
  const [leadCategory, setLeadCategory] = useState(initialLeadCategory);
  const [otherText, setOtherText] = useState("");
  const [referringDoctor, setReferringDoctor] = useState("");
  const [date, setDate] = useState(torontoToday); // YYYY-MM-DD in Toronto time

  const createPatientMutation = useCreatePatient();

  const isOther = leadCategory === "Other";
  const isDoctorReferral = leadCategory === "Doctor/GDP Referral";
  const canSubmit =
    patientName.trim().length > 0 &&
    clinicId &&
    (!isOther || otherText.trim().length > 0) &&
    (!isDoctorReferral || referringDoctor.trim().length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      // Create patient via API
      const patientData = {
        name: patientName.trim(),
        lead: isOther
          ? `Other: ${otherText.trim()}`
          : isDoctorReferral
            ? `Doctor/GDP Referral: ${referringDoctor.trim()}`
            : leadCategory,
        clinic: clinicId,
        date: date,
      };

      await createPatientMutation.mutateAsync(patientData);

      recordSubmitSuccess();

      // Show success toast
      toast.success("Patient created successfully!");

      // Reset form after successful submit
      setPatientName("");
      setLeadCategory(LEAD_CATEGORIES[0]);
      setOtherText("");
      setReferringDoctor("");
      setDate(torontoToday());
    } catch (error) {
      console.error("Failed to create patient:", error);
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <FormSubmit onSubmit={handleSubmit} className="mx-auto max-w-2xl" {...formTrackingProps}>
      <FormField
        label="Patient Name"
        placeholder="Patient name"
        value={patientName}
        onChange={(e) => setPatientName(e.target.value)}
        required
        className="mb-5"
      />

      <Dropdown
        label="Lead Category"
        value={leadCategory}
        onChange={(value) => setLeadCategory(value)}
        options={LEAD_CATEGORIES}
        className="mb-5"
      />

      {isDoctorReferral && (
        <FormField
          label="Referring Doctor Name"
          placeholder="Enter referring doctor's name"
          value={referringDoctor}
          onChange={(e) => setReferringDoctor(e.target.value)}
          required
          className="mb-5"
        />
      )}

      {isOther && (
        <FormField
          label="Please specify"
          placeholder="Enter lead category"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          required
          className="mb-5"
        />
      )}

      <FormField
        label="Date"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        required
        className="mb-8"
      />

      <Button
        as="button"
        type="submit"
        variant="gradient-success"
        disabled={!canEdit || !canSubmit || createPatientMutation.isPending}
        className="w-full rounded-full px-8 py-3 font-semibold disabled:opacity-40"
      >
        {createPatientMutation.isPending ? "Submitting..." : "Submit"}
      </Button>

      <MutationError
        show={createPatientMutation.isError}
        error={createPatientMutation.error}
        fallbackMessage="Failed to create patient"
        className="mt-3"
      />
    </FormSubmit>
  );
}
